import { DatePipe, NgClass } from '@angular/common';
import {
  Component,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatDivider } from '@angular/material/divider';
import { MatFormField, MatInput, MatPrefix } from '@angular/material/input';
import { MatMenu, MatMenuTrigger } from '@angular/material/menu';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, MatSortHeader, Sort } from '@angular/material/sort';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable
} from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { ConfirmationDialogComponent } from '../../components/confirmation-dialog/confirmation-dialog.component';
import { InlineSvgComponent } from '../../components/inline-svg/inline-svg.component';
import { ShareModalComponent } from '../../components/share-modal/share-modal.component';
import { UserLogoComponent } from '../../components/user-logo/user-logo.component';
import { RiskReport } from '../../models/report.model';
import { User } from '../../models/user.model';
import { TruncatePipe } from '../../pipes/truncate.pipe';
import { NewReportService } from '../../services/newReport.service';
import { NotificationService } from '../../services/notification.service';

@Component({
  selector: 'frmg-report-table',
  templateUrl: './report-table.component.html',
  styleUrls: ['./report-table.component.scss'],
  imports: [
    InlineSvgComponent,
    MatFormField,
    MatPrefix,
    MatInput,
    ReactiveFormsModule,
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    MatTooltip,
    UserLogoComponent,
    NgClass,
    MatMenuTrigger,
    MatMenu,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatDivider,
    MatPaginator,
    DatePipe,
    TruncatePipe
  ]
})
export class ReportTableComponent implements OnInit, OnDestroy {
  public displayedColumns = [
    'name',
    'created_at',
    'status',
    'shared',
    'action'
  ];
  public reportData: Array<RiskReport> = [];
  public currentUser: User;
  public currentPage = 1;
  public pageSize = 20;
  public totalElements = 0;
  private updateTimeout: any = null;
  private sort: Sort = { active: 'created_at', direction: 'desc' };
  public reportSearchCtrl: FormControl = new FormControl<string>('');
  public errorText = 'No data for the selected range';

  public menuOpen: MatMenuTrigger;
  public hoverId: number;
  public menuState: string;

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private reportService: NewReportService,
    private router: Router,
    private dialog: MatDialog,
    private notificationService: NotificationService
  ) {
    this.currentUser = JSON.parse(localStorage.getItem('user')) as User;
  }

  ngOnDestroy(): void {
    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout);
      this.updateTimeout = null;
    }
  }

  ngOnInit() {
    this.getReports();
    this.reportSearchCtrl.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.getReports();
      });
  }

  sortData(sort: Sort) {
    this.sort = sort;
    this.getReports();
  }

  public moreUsers(project: RiskReport): User {
    if (project.shared_with.length == 3) {
      return project.shared_with[2];
    } else {
      return {
        id: '0',
        teams: [],
        email: `+${project.shared_with.length - 2}`
      };
    }
  }

  private getReports(): void {
    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout);
      this.updateTimeout = null;
    }
    const config: any = { page: this.currentPage, size: this.pageSize };
    config.order_by =
      (this.sort.direction === 'asc' ? '' : '-') + this.sort.active;
    const searchStr = this.reportSearchCtrl.value.toLowerCase();
    if (searchStr) {
      config.search = searchStr;
    }
    this.reportService
      .getReports(config)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        this.totalElements = result.total;
        this.reportData = result.items;

        if (
          this.reportData.some(
            (report) => report.status.toLowerCase() === 'processing'
          )
        ) {
          this.updateTimeout = setTimeout(() => this.getReports(), 3000);
        }
      });
  }

  public downloadPdf(
    id: string,
    filename: string = 'report',
    _event: Event
  ): void {
    this.reportService.getReportPdf(id, filename).subscribe((data) => {
      this.notificationService.updateDownload({
        fileName: `${filename}.pdf`,
        percentage: data.progress,
        total: data.total,
        loaded: data.loaded
      });
    });
  }

  public downloadXLSX(
    id: string,
    filename: string = 'report',
    _event: Event
  ): void {
    this.reportService.getReportXLSX(id, filename).subscribe((data) => {
      this.notificationService.updateDownload({
        fileName: `${filename}.xlsx`,
        percentage: data.progress,
        total: data.total,
        loaded: data.loaded
      });
    });
  }

  public share(report: RiskReport, event: Event): void {
    event.stopPropagation();
    this.dialog
      .open(ShareModalComponent<RiskReport>, {
        data: {
          entity: report
        }
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.getReports();
      });
  }

  public deleteReport(report: RiskReport, event: Event): void {
    event.stopPropagation();
    this.dialog
      .open<ConfirmationDialogComponent>(ConfirmationDialogComponent, {
        width: '640px',
        data: {
          header: 'Are you sure you want to delete this report?',
          body: 'Deleting this report will permanently remove it and it cannot be restored. Users with access will no longer be able to view this report.',
          confirmText: 'Delete'
        }
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((del) => {
        if (del)
          this.reportService.deleteReport(report.id).subscribe(() => {
            this.getReports();
          });
      });
  }
  public unShareReport(report: RiskReport, event: Event): void {
    event.stopPropagation();
    this.dialog
      .open<ConfirmationDialogComponent>(ConfirmationDialogComponent, {
        width: '640px',
        data: {
          header: 'Are you sure you want to remove this report from the list?',
          body: 'This report will be removed from your list. Users with access will still be able to view it.',
          confirmText: 'Yes, remove folder',
          cancelText: 'No'
        }
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((del) => {
        if (del)
          this.reportService
            .leaveSharedProject()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
              this.getReports();
            });
      });
  }

  public getUsersWithAccess(report: RiskReport) {
    switch (this.currentUser.email) {
      case report.user?.email:
        return report.shared_with;
      default:
        return [];
    }
  }

  public handlePageEvent(e: PageEvent) {
    this.pageSize = e.pageSize;
    this.currentPage = e.pageIndex + 1;
    this.getReports();
  }

  public goToReport(report: RiskReport) {
    // if (report.status.toLowerCase() === 'completed') {
    this.router.navigate([`/report/${report.id}`]);
    // }
  }

  public statusClass(report: RiskReport) {
    switch (report.status.toLowerCase()) {
      case 'completed':
        return 'completed';
      case 'processing':
        return 'processing';
      default:
        return 'failed';
    }
  }

  public reportTrackBy(index: number, report: RiskReport) {
    return report.id + report.progress;
  }

  public setHoverId(index: number | null) {
    if (this.menuState) return;
    this.hoverId = index;
  }

  public toggleMenu(state: RiskReport, event?: MouseEvent) {
    event && event.stopPropagation();
    this.menuState = state?.id;
  }
}
